"use client"

import { postMemberRegistration } from "@/services/member-registration-service"
import type { MemberRequest } from "@/types/codegen/data-contracts"
import type { BecomeMemberNote, BecomeMemberSelectOption } from "../types"
import { SignatureCanvasArea, type SignatureCanvasHandle } from "./SignatureCanvasArea"
import { TdfRadio } from "./TdfRadio"
import "./styles.css"
import type { FormEvent, ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { getFixedT } from "@/languages/i18n"

type RegistrationFormProps = {
  onSubmitted: (email?: string) => void
  submittedMessage?: string
  registrationEmail?: string
  registrationCode?: string
  noteTitle?: string
  notes?: BecomeMemberNote[]
  countryOptions?: BecomeMemberSelectOption[]
  storeOptions?: BecomeMemberSelectOption[]
  locale?: string
}

type FieldProps = {
  name: string
  label?: string
  required?: boolean
  error?: string
  value?: string
  type?: string
  placeholder?: string
  options?: BecomeMemberSelectOption[]
  multiline?: boolean
  action?: ReactNode
  onChange: (name: string, value: string) => void
}

const COPY_ICON = "/images/become-member/copy.png"
const PRINT_ICON = "/images/become-member/print.png"
const VAT_REQUIRED_FIELDS = ["ico", "businessName", "phone", "email", "address"]
const NON_VAT_REQUIRED_FIELDS = ["idNumber", "fullname", "occupation", "phone", "email", "address"]
const EMAIL_PATTERN = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

type ReceiveSMSValue = NonNullable<MemberRequest["data"]["receiveSMS"]>

const Field = ({
  name,
  label,
  required = false,
  error,
  value = "",
  type = "text",
  placeholder,
  options,
  multiline = false,
  action,
  onChange,
}: FieldProps) => {
  const id = `tdf-${name}`

  return (
    <div className="tdf-field">
      {label && (
        <label className="tdf-field-label" htmlFor={id}>
          {label}
          {required && <span className="tdf-field-asterisk">*</span>}
        </label>
      )}

      <div className="tdf-field-control-wrap">
        {options ? (
          <select
            id={id}
            className="tdf-field-control tdf-field-select"
            value={value}
            onChange={event => onChange(name, event.target.value)}
          >
            {options.map(option => (
              <option key={`${option.value}-${option.label}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : multiline ? (
          <textarea
            id={id}
            className="tdf-field-control tdf-field-textarea"
            placeholder={placeholder}
            value={value}
            onChange={event => onChange(name, event.target.value)}
          />
        ) : (
          <input
            id={id}
            className="tdf-field-control"
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={event => onChange(name, event.target.value)}
          />
        )}

        {action}
      </div>

      {error && <p className="tdf-field-error">{error}</p>}
    </div>
  )
}

const trimValue = (value?: string) => value?.trim() || ""

const generateRegistrationCode = (baseCode?: string) => {
  const prefix = baseCode?.split("-")[0]?.trim() || "PRAHA"
  return `${prefix}-${Math.floor(1000 + Math.random() * 9000)}`
}

const getSignatureFileName = (values: Record<string, string>) => {
  const name = trimValue(values.ico) || trimValue(values.fullname) || trimValue(values.email) || "member"
  return `signature_${name.replace(/[^a-zA-Z0-9._-]/g, "_")}.png`
}

const normalizeReceiveSMS = (value: string): ReceiveSMSValue => {
  if (value === "cz" || value === "vn" || value === "en") return value
  return "no"
}

export const RegistrationForm = ({
  onSubmitted,
  submittedMessage,
  registrationEmail = "longtran@gmail.com",
  registrationCode = "PRAHA-2657",
  noteTitle,
  notes = [],
  countryOptions = [],
  storeOptions = [],
  locale,
}: RegistrationFormProps) => {
  const t = getFixedT(locale)
  const tSubmittedMessage = submittedMessage ?? t("Your registration information has been sent to your email:")
  const tNoteTitle = noteTitle ?? t("NOTE:")
  const [hasVat, setHasVat] = useState(true)
  const [receive, setReceive] = useState("no")
  const [registered, setRegistered] = useState("yes")
  const [nextStep, setNextStep] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [didDraw, setDidDraw] = useState(false)
  const [hasSubmittedAttempt, setHasSubmittedAttempt] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [submittedCode, setSubmittedCode] = useState(registrationCode)
  const codeLabelRef = useRef<HTMLParagraphElement | null>(null)
  const signaturePadRef = useRef<SignatureCanvasHandle | null>(null)

  const normalizedCountries = countryOptions.length
    ? countryOptions
    : [
        { label: "VietNam", value: "vn" },
        { label: "US", value: "us" },
      ]
  const normalizedStores = storeOptions.length
    ? storeOptions
    : [
        { label: "Location at Hansinky", value: "hansinky" },
        { label: "Location at Timo", value: "timo" },
      ]

  const buildFieldErrors = (
    nextValues: Record<string, string>,
    nextHasVat = hasVat,
    nextDidDraw = didDraw,
    nextAgreeTerms = agreeTerms,
  ) => {
    const requiredFields = nextHasVat ? VAT_REQUIRED_FIELDS : NON_VAT_REQUIRED_FIELDS

    const errors = requiredFields.reduce<Record<string, string>>((currentErrors, fieldName) => {
      if (!nextValues[fieldName]?.trim()) {
        currentErrors[fieldName] = t("This field is required")
      }

      return currentErrors
    }, {})

    if (nextValues.email?.trim() && !EMAIL_PATTERN.test(nextValues.email.trim())) {
      errors.email = t("Please enter a valid email")
    }

    if (!nextDidDraw) {
      errors.signature = t("You must draw your signature")
    }

    if (!nextAgreeTerms) {
      errors.agreeTerms = t("You must accept Terms of service and Privacy policy")
    }

    return errors
  }

  const handleVatChange = (nextHasVat: boolean) => {
    signaturePadRef.current?.clear()
    setDidDraw(false)
    setHasVat(nextHasVat)
    setFieldErrors(hasSubmittedAttempt ? buildFieldErrors(values, nextHasVat, false) : {})
  }

  const handleSignatureBegin = () => {
    setDidDraw(true)
    if (hasSubmittedAttempt) {
      setFieldErrors(buildFieldErrors(values, hasVat, true))
    }
  }

  const handleSignatureClear = () => {
    setDidDraw(false)
    if (hasSubmittedAttempt) {
      setFieldErrors(buildFieldErrors(values, hasVat, false))
    }
  }

  const handleAgreeTermsChange = (checked: boolean) => {
    setAgreeTerms(checked)
    if (hasSubmittedAttempt) {
      setFieldErrors(buildFieldErrors(values, hasVat, didDraw, checked))
    }
  }

  const handleChange = (name: string, value: string) => {
    const nextValues = { ...values, [name]: value }

    setValues(nextValues)
    setSubmitError("")

    if (hasSubmittedAttempt) {
      setFieldErrors(buildFieldErrors(nextValues))
    }
  }

  const handleFetchIcoData = async () => {
    const icoValue = values.ico?.trim()

    if (!icoValue || !/^\d{8}$/.test(icoValue)) {
      return
    }

    try {
      const response = await fetch(`/api/ico-data?ico=${encodeURIComponent(icoValue)}`)
      const result = await response.json()

      if (!response.ok || !result?.success || !result?.data) {
        return
      }

      setValues(previous => ({
        ...previous,
        businessName: result.data.companyName || previous.businessName || "",
        dic: result.data.dic || previous.dic || "",
        address: result.data.address || previous.address || "",
      }))
    } catch (error) {
      console.error("Failed to fetch ICO data", error)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const nextErrors = buildFieldErrors(values)
    setHasSubmittedAttempt(true)
    setFieldErrors(nextErrors)
    setSubmitError("")

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    const signatureDataUrl = signaturePadRef.current?.toDataURL()
    if (!signatureDataUrl) {
      setFieldErrors(previous => ({ ...previous, signature: t("You must draw your signature") }))
      return
    }

    const code = generateRegistrationCode(registrationCode)
    const commonData = {
      phone: trimValue(values.phone),
      email: trimValue(values.email),
      address: trimValue(values.address),
      city: trimValue(values.city),
      countryOrState: trimValue(values.countryOrState) || normalizedCountries[0]?.value || "",
      receiveAtSore: trimValue(values.receiveAtSore) || normalizedStores[0]?.value || "",
      haveVAT: hasVat,
      dphRegistered: hasVat ? registered === "yes" : false,
      receiveSMS: normalizeReceiveSMS(receive),
      registrationCode: code,
      isSentEmail: false,
      isResolved: false,
    } satisfies Partial<MemberRequest["data"]>

    const memberData: MemberRequest["data"] = hasVat
      ? {
          ...commonData,
          ico: trimValue(values.ico),
          dic: trimValue(values.dic),
          businessName: trimValue(values.businessName),
          businessRepresentative: trimValue(values.businessRepresentative),
        }
      : {
          ...commonData,
          birthNumber: trimValue(values.birthNumber),
          fullname: trimValue(values.fullname),
          idNumber: trimValue(values.idNumber),
          occupation: trimValue(values.occupation),
        }

    try {
      setIsSubmitting(true)
      await postMemberRegistration({
        data: memberData,
        signatureDataUrl,
        signatureFileName: getSignatureFileName(values),
      })

      setSubmittedCode(code)
      setNextStep(1)
      onSubmitted(trimValue(values.email) || registrationEmail)
    } catch (error) {
      console.error("Failed to submit member registration", error)
      setSubmitError(error instanceof Error ? error.message : t("Something went wrong, please try again."))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard?.writeText(submittedCode)
    } catch (error) {
      console.error("Failed to copy registration code", error)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  useEffect(() => {
    if (nextStep === 1) {
      codeLabelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [nextStep])

  const showSubmittedForm = nextStep === 1

  return (
    <div className={`tdf-registration-form ${showSubmittedForm ? "tdf-registration-form-submitted" : ""}`}>
      {!showSubmittedForm && (
        <div className="tdf-vat-selector">
          <TdfRadio
            checked={hasVat}
            onChange={() => handleVatChange(true)}
            label={<span className="tdf-radio-strong">{t("Have VAT")}</span>}
          />

          <TdfRadio
            checked={!hasVat}
            onChange={() => handleVatChange(false)}
            label={<span className="tdf-radio-strong">{t("Haven't got VAT")}</span>}
          />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {!showSubmittedForm && hasVat && (
          <>
            <div className="tdf-form-row">
              <Field
                name="ico"
                label={t("IČO")}
                required
                error={fieldErrors.ico}
                value={values.ico}
                onChange={handleChange}
                action={
                  <button className="tdf-get-data-button" type="button" onClick={handleFetchIcoData}>
                    {t("Get Data")}
                  </button>
                }
              />
              <Field name="dic" label={t("DIČ")} value={values.dic} onChange={handleChange} />
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field
                name="businessName"
                label={t("Business Name")}
                required
                error={fieldErrors.businessName}
                value={values.businessName}
                onChange={handleChange}
              />
              <Field
                name="businessRepresentative"
                label={t("Business Representative")}
                value={values.businessRepresentative}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-inline-choice-row">
              <p className="tdf-inline-choice-title">{t("Registered for DPH yet?")}</p>
              <div className="tdf-inline-choice-options tdf-inline-choice-options-wide">
                <TdfRadio
                  checked={registered === "yes"}
                  onChange={() => setRegistered("yes")}
                  label={<span className="tdf-radio-strong">{t("Yes")}</span>}
                />
                <TdfRadio
                  checked={registered === "no"}
                  onChange={() => setRegistered("no")}
                  label={<span className="tdf-radio-strong">{t("Not yet")}</span>}
                />
              </div>
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field
                name="phone"
                label={t("Phone number")}
                required
                error={fieldErrors.phone}
                value={values.phone}
                onChange={handleChange}
              />
              <Field
                name="email"
                label={t("Email address")}
                required
                error={fieldErrors.email}
                value={values.email}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row-single">
              <Field
                name="address"
                label={t("Address")}
                required
                error={fieldErrors.address}
                value={values.address}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field name="city" label={t("City/Province")} value={values.city} onChange={handleChange} />
              <Field
                name="countryOrState"
                label={t("Country/State")}
                options={normalizedCountries}
                value={values.countryOrState || normalizedCountries[0]?.value}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row-single">
              <Field
                name="receiveAtSore"
                label={t("Receive at Store")}
                options={normalizedStores}
                value={values.receiveAtSore || normalizedStores[0]?.value}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-inline-choice-row tdf-sms-choice-row">
              <p className="tdf-inline-choice-title">{t("Receive marketing SMS?")}</p>
              <div className="tdf-inline-choice-options">
                <TdfRadio
                  checked={receive === "no"}
                  onChange={() => setReceive("no")}
                  label={<span className="tdf-radio-strong">{t("No, thanks.")}</span>}
                />
                <TdfRadio
                  checked={receive === "cz"}
                  onChange={() => setReceive("cz")}
                  label={<span className="tdf-radio-strong">CZ</span>}
                />
                <TdfRadio
                  checked={receive === "vn"}
                  onChange={() => setReceive("vn")}
                  label={<span className="tdf-radio-strong">VN</span>}
                />
              </div>
            </div>

          </>
        )}

        {!showSubmittedForm && !hasVat && (
          <>
            <div className="tdf-form-row">
              <Field name="birthNumber" label={t("Birth Number")} value={values.birthNumber} onChange={handleChange} />
              <Field
                name="idNumber"
                label={t("Id Number")}
                required
                error={fieldErrors.idNumber}
                value={values.idNumber}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field
                name="fullname"
                label={t("Fullname")}
                required
                error={fieldErrors.fullname}
                value={values.fullname}
                onChange={handleChange}
              />
              <Field
                name="occupation"
                label={t("Occupation")}
                required
                error={fieldErrors.occupation}
                value={values.occupation}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field
                name="phone"
                label={t("Phone number")}
                required
                error={fieldErrors.phone}
                value={values.phone}
                onChange={handleChange}
              />
              <Field
                name="email"
                label={t("Email address")}
                required
                error={fieldErrors.email}
                value={values.email}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row-single">
              <Field
                name="address"
                label={t("Address")}
                required
                error={fieldErrors.address}
                value={values.address}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row tdf-form-row-spaced">
              <Field name="city" label={t("City/Province")} value={values.city} onChange={handleChange} />
              <Field
                name="countryOrState"
                label={t("Country/State")}
                options={normalizedCountries}
                value={values.countryOrState || normalizedCountries[0]?.value}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-form-row-single">
              <Field
                name="receiveAtSore"
                label={t("Receive at Store")}
                options={normalizedStores}
                value={values.receiveAtSore || normalizedStores[0]?.value}
                onChange={handleChange}
              />
            </div>

            <div className="tdf-inline-choice-row tdf-sms-choice-row">
              <p className="tdf-inline-choice-title">{t("Receive marketing SMS?")}</p>
              <div className="tdf-inline-choice-options">
                <TdfRadio
                  checked={receive === "no"}
                  onChange={() => setReceive("no")}
                  label={<span className="tdf-radio-strong">{t("No, thanks.")}</span>}
                />
                <TdfRadio
                  checked={receive === "cz"}
                  onChange={() => setReceive("cz")}
                  label={<span className="tdf-radio-strong">CZ</span>}
                />
                <TdfRadio
                  checked={receive === "vn"}
                  onChange={() => setReceive("vn")}
                  label={<span className="tdf-radio-strong">VN</span>}
                />
              </div>
            </div>

          </>
        )}

        {!showSubmittedForm && (
          <div className="tdf-signature-row">
            <p className="tdf-signature-label">{t("Signature")}</p>
            <SignatureCanvasArea
              ref={signaturePadRef}
              error={fieldErrors.signature}
              onBegin={handleSignatureBegin}
              onClear={handleSignatureClear}
            />
          </div>
        )}

        {showSubmittedForm && (
          <div className="tdf-submitted-content">
            <p className="tdf-submitted-message">
              {tSubmittedMessage} <strong>{trimValue(values.email) || registrationEmail}</strong>
            </p>
            <p className="tdf-registration-code-label" ref={codeLabelRef}>
              {t("Registration Code")}
            </p>

            <div className="tdf-code-row">
              <div className="tdf-code-box">
                <p className="tdf-code-text">{submittedCode}</p>
                <button className="tdf-code-action tdf-code-action-print" type="button" onClick={handlePrint}>
                  <img src={PRINT_ICON} alt="print" width={30} height={28} />
                </button>
                <button className="tdf-code-action tdf-code-action-copy" type="button" onClick={handleCopyCode}>
                  <img src={COPY_ICON} alt="copy" width={30} height={30} />
                </button>
              </div>
            </div>

            <div className="tdf-divider" />

            <div className="tdf-note-box">
              <p className="tdf-note-title">{tNoteTitle}</p>
              <div className="tdf-note-list">
                {notes.map((note, index) => (
                  <p key={index} className="tdf-note-item">
                    &#x2022; {note.text}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {!showSubmittedForm && (
          <>
            <div className="tdf-policy-row">
              <label className="tdf-policy">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={event => handleAgreeTermsChange(event.target.checked)}
                />
                <span>{t("I agree to Terms of service and Privacy policy")}</span>
              </label>
            </div>
            {fieldErrors.agreeTerms && <p className="tdf-policy-error">{fieldErrors.agreeTerms}</p>}
            {submitError && <p className="tdf-submit-error">{submitError}</p>}

            <div className="tdf-submit-row">
              <button className="tdf-submit-button" type="submit" disabled={isSubmitting}>
                <span>{isSubmitting ? t("Submitting...") : t("Submit registration")}</span>
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  )
}
